import type { InputContainerProps } from '../InputContainer/index.ts';
import clsx from 'clsx';
import { useCallback } from 'react';
import { LuChevronsUpDown } from 'react-icons/lu';
import { InputContainer } from '../InputContainer/index.ts';
import { useTextInputGroup } from '../TextInput/index.ts';

export interface NativeSelectProps extends Pick<
  InputContainerProps,
  'cornerText' | 'error' | 'helpText' | 'label'
> {
  children: React.ReactNode;
  disabled?: boolean;
  onChange?: (value: string | null) => void;
  renderTrigger?: (value: string | null) => React.ReactNode;
  value: string | null;
}

export function NativeSelect({
  children,
  cornerText,
  disabled = false,
  error,
  helpText,
  label,
  onChange,
  renderTrigger,
  value
}: NativeSelectProps) {
  const inputGroup = useTextInputGroup();

  const handleChange = useCallback<React.ChangeEventHandler<HTMLSelectElement>>(
    event => {
      onChange?.(event.target.value || null);
    },
    [onChange]
  );

  const triggerClasses = clsx([
    // Basic layout
    'relative block w-full py-[calc(--spacing(1.5)-1px)]',

    // Horizontal padding
    'pr-[calc(--spacing(10)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pr-[calc(--spacing(9)-1px)] sm:pl-[calc(--spacing(3)-1px)]',

    // Typography
    'text-base/6 sm:text-sm/6',
    !value ? 'text-gray-500' : 'text-gray-950',

    // Border
    'border-gray-950/10',
    !disabled && !error && 'group-hover:border-gray-950/20',
    !inputGroup && 'border-t border-b shadow-xs',
    !inputGroup?.isFirst && 'border-l',
    !inputGroup?.isLast && 'border-r',
    (!inputGroup || inputGroup.isFirst) && 'rounded-l-md',
    (!inputGroup || inputGroup.isLast) && 'rounded-r-md',

    // Background color
    'bg-transparent',

    // Invalid state
    !!error && 'border-red-500',

    // Disabled state
    disabled && 'border-gray-950/20 opacity-50'
  ]);

  return (
    <InputContainer cornerText={cornerText} error={error} helpText={helpText} label={label}>
      <div className="group relative">
        <select
          aria-invalid={!!error}
          className={clsx(
            'peer cursor-pointer appearance-none outline-hidden focus:outline-hidden',
            renderTrigger ? 'absolute inset-0 z-10 size-full opacity-0' : triggerClasses
          )}
          disabled={disabled}
          onChange={handleChange}
          value={value ?? ''}
        >
          {children}
        </select>

        {renderTrigger && (
          <div aria-hidden className={clsx('pointer-events-none', triggerClasses)}>
            {renderTrigger(value)}
          </div>
        )}

        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <LuChevronsUpDown
            className={clsx('size-5 text-gray-500 sm:size-4', disabled && 'opacity-50')}
          />
        </span>

        {!inputGroup && (
          <span className="pointer-events-none absolute inset-0 z-20 rounded-md ring-transparent ring-inset peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500" />
        )}
      </div>
    </InputContainer>
  );
}

export function NativeSelectOption(props: React.ComponentPropsWithRef<'option'>) {
  return <option {...props} />;
}
